/**
 * AppShell — layout mobile-first do area do cliente.
 * Topbar com logo do tenant + bottom nav fixa estilo app.
 *
 * Segundo ciclo de polish: indicador animado de item ativo, scale + glow no
 * ícone selecionado, topbar com depth ao rolar (glass + shadow), transição
 * suave entre rotas via .octa-route-enter, safe-area no bottom-nav.
 */
import { useQuery } from '@tanstack/react-query';
import { Bell, CarFront, Home as HomeIcon, MapPin, ScanLine, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { SkipLink } from '@/components/skip-link';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';
import { useNotificacoesRealtime } from '@/lib/use-notificacoes-realtime';
import { cn } from '@/lib/utils';

const NAV_ITEMS = [
  { label: 'Início', href: '/', icon: HomeIcon },
  { label: 'Veículos', href: '/veiculos', icon: CarFront },
  { label: 'Código', href: '/gerar-codigo', icon: ScanLine, featured: true },
  { label: 'Mapa', href: '/mapa', icon: MapPin },
  { label: 'Perfil', href: '/perfil', icon: User },
];

export function AppShell() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  // Detecta scroll pra dar depth na topbar (glass + shadow)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Subscreve em mudancas via Realtime — atualiza badge no ato + toast em INSERT
  // Silencia toast quando usuário já está vendo a lista de notificações
  useNotificacoesRealtime({ silenciarToast: location.pathname.startsWith('/notificacoes') });

  const naoLidas = useQuery({
    queryKey: ['notif-unread', user?.id],
    queryFn: async () => {
      const { count } = await supabase
        .from('tab_notificacoes_in_app')
        .select('seq_notificacao', { count: 'exact', head: true })
        .eq('cod_auth_user', user!.id)
        .eq('ind_lida', false);
      return count ?? 0;
    },
    enabled: !!user,
    refetchInterval: 30_000,
  });

  return (
    <div className="bg-background text-foreground flex min-h-screen flex-col">
      <SkipLink />
      <header
        className={cn(
          'sticky top-0 z-40 flex h-14 items-center justify-between px-4 transition-all duration-300',
          scrolled ? 'octa-topbar-scrolled' : 'bg-background/95 border-b border-transparent',
        )}
      >
        <div className="flex items-center gap-2.5">
          {tenant?.des_logo_url ? (
            <img
              src={tenant.des_logo_url}
              alt={tenant.nom_tenant}
              className="shadow-octa-sm h-9 w-9 rounded-xl object-cover ring-1 ring-black/5"
            />
          ) : (
            <div className="bg-octa-gradient text-primary-foreground shadow-octa-sm flex h-9 w-9 items-center justify-center rounded-xl text-xs font-extrabold ring-1 ring-black/5">
              {(tenant?.nom_tenant ?? 'OW').slice(0, 2).toUpperCase()}
            </div>
          )}
          <span className="text-sm font-bold tracking-tight">
            {tenant?.nom_tenant ?? 'Octa Pontos'}
          </span>
        </div>
        <Link
          to="/notificacoes"
          className="text-muted-foreground hover:text-foreground octa-tap hover:bg-accent/50 relative flex h-10 w-10 items-center justify-center rounded-xl transition-colors active:scale-95"
          aria-label={`Notificações${naoLidas.data ? ` (${naoLidas.data} nao lidas)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {naoLidas.data && naoLidas.data > 0 ? (
            <span className="bg-octa-accent shadow-octa-glow ring-background absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white ring-2">
              {naoLidas.data > 9 ? '9+' : naoLidas.data}
            </span>
          ) : null}
        </Link>
      </header>

      <main
        id="main-content"
        tabIndex={-1}
        key={location.pathname}
        className="octa-route-enter flex-1 pb-24 focus:outline-none"
      >
        <Outlet />
      </main>

      <nav
        className="octa-glass-premium border-border/40 pb-safe fixed bottom-0 left-0 right-0 z-50 grid grid-cols-5 border-t"
        aria-label="Navegacao principal"
      >
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            className={({ isActive }) =>
              cn(
                'octa-tap relative flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition-colors',
                isActive
                  ? cn('text-primary octa-nav-active', item.featured && 'text-octa-gradient')
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            {({ isActive }) => (
              <>
                <div
                  className={cn(
                    'ease-octa-out flex h-8 w-8 items-center justify-center rounded-xl transition-all duration-300',
                    isActive
                      ? item.featured
                        ? 'bg-octa-gradient shadow-octa-glow scale-110 text-white'
                        : 'bg-primary/10 text-primary scale-105'
                      : 'scale-100',
                  )}
                >
                  <item.icon
                    className={cn('transition-all', isActive ? 'h-[18px] w-[18px]' : 'h-5 w-5')}
                    strokeWidth={isActive ? 2.4 : 2}
                  />
                </div>
                <span className={cn('transition-all', isActive ? 'opacity-100' : 'opacity-70')}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
